import * as React from "react";
import { useEffect, useState } from "react";
import { Slide, styled } from "@mui/material";
import { useDiseasesSections } from "./hooks/useDiseasesSections";
import { Suggestion } from "./hooks/useSuggestions";
import { DiseaseCategoryType } from "fe-common/graphql";
import { RecommendedPlanSection } from "./recommendedPlan/recommendedPlanSection";
import { AgentRoute } from "./util/agentRoutes";
import { CATEGORY } from "fe-common/analytics/analytics";
import { usePatientCase } from "./usePatientCase";
import {
  MobileView,
  SingleViewPerLayoutBreakPoint,
} from "../../util/aKaunGeneralUtil";
import { BackButton } from "./common/backButton";
import { RosebudMobileBottomNavigationHeight } from "./common/rosebudButtomNavigationButton";
import { ThreeDotsLoader } from "fe-web-components/loader/threeDotsLoader";
import { useGetDiseasesInPlan } from "./hooks/useGetDiseasesInPlan";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { useDiagnosticNavigation } from "./useDiagnosticNavigation";
import { useRouteHelper } from "./useRouteHelper";
import { useSuggestionsForPlan } from "./hooks/useSuggestionsForPlan";
import { useScreenSize } from "./util/useScreenSize";
import { MobileOnly } from "./util/mobileOnly";
import { HowItWorksTopLink } from "./santana/howItWorksTopLink";

//region [[ Styles ]]
const HistoryAndPhysicalView = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  padding: "6px",
  width: "100%",
  paddingTop: "0px",
  minHeight: "300px",
  [theme.breakpoints.down(MobileView)]: {
    margin: "-7px", //Kill content wrapper padding
    marginBottom: `${RosebudMobileBottomNavigationHeight + 6}px`, // Keep space for bottom nav button
    padding: 0,
    width: "auto", // since 100% is excluded the -7px we took from right and left
    minHeight: "100%",
    background: KahunColorMap.Secondary,
  },
}));

const TitleArea = styled("div")(({ theme }) => ({
  disaply: "flex",
  flexFlow: "column",
  width: "100%",
}));

const HowItWorks = styled(HowItWorksTopLink)({
  padding: "20px 24px 9px",
});

const TitleContainer = styled("div")(({ theme }) => ({
  display: "flex",
  padding: "7px",
  paddingTop: "0px",
  flexDirection: "row",
  justifyContent: "start",
  alignItems: "center",
  width: "100%",
  [theme.breakpoints.down(MobileView)]: {
    padding: "5px",
    backgroundColor: KahunColorMap.Secondary,
    justifyContent: "start",
    color: "white",
    minHeight: "57px",
    width: "auto",
  },
}));

const Title = styled("div")(({ theme }) => ({
  fontSize: "24px",
  fontWeight: 500,
  [theme.breakpoints.down(MobileView)]: {
    fontWeight: 700,
    fontSize: "18px",
  },
}));
const BackButtonContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.up(SingleViewPerLayoutBreakPoint)]: {
    display: "none",
  },
  "& .back-button svg": {
    color: "white",
  },
}));

const NoSuggestionsText = styled("div")({
  display: "flex",
  width: "100%",
  height: "100%",
  flexFlow: "column",
  justifyContent: "center",
  textAlign: "center",
  color: KahunColorMap.DefaultDark,
  minHeight: "100px",
  padding: "24px",
});

const HistoryAndPhysicalContainer = styled("div")(({ theme }) => ({}));
//endregion [[ Styles ]]

//region [[ Props ]]
//endregion [[ Props ]]

//region [[ Functions ]]

//endregion [[ Functions ]]
const maxItems = 10;

export const HistoryAndPhysical = () => {
  const [changes, setChanges] = useState([]);

  const { doChangesToPatientCase, doChangesToPending } = usePatientCase();

  let {
    findingSuggestions: nextStepSuggestions,
    planIsLoading,
  } = useSuggestionsForPlan();

  const { loading: ddIsLoading } = useDiseasesSections();

  const { isMobile } = useScreenSize();
  const changeImmediately = !isMobile;

  const { setNextRouteNavigationDisabled } = useDiagnosticNavigation();

  const { navigateTo } = useRouteHelper();

  const diseasesInPlan = useGetDiseasesInPlan();

  const loading = ddIsLoading || planIsLoading;

  // Keep the diagnose button disabled make sure to enable it on unmount
  useEffect(() => {
    setNextRouteNavigationDisabled(true);
    return () => setNextRouteNavigationDisabled(false);
  }, [setNextRouteNavigationDisabled]);

  const filterSuggestions = (suggestion: Suggestion) => {
    if (suggestion.category === DiseaseCategoryType.OutcomeTest) {
      return true;
    }
    return (
      (!suggestion.inPatient && !suggestion.finding.qualifiers.length) ||
      (suggestion.inPatient && suggestion.finding.qualifiers.length)
    );
  };

  const filteredNextStepSuggestions = nextStepSuggestions.filter(
    filterSuggestions,
  );

  const noResults = filteredNextStepSuggestions.length === 0;

  const addChange = (change: any) => {
    if (changeImmediately) {
      doChangesToPending([change]);
    } else {
      const newChanges = [...changes, change];
      setChanges(newChanges);
      doChangesToPatientCase(newChanges);
      setNextRouteNavigationDisabled(false);
    }
  };

  const goBack = () => {
    navigateTo({ route: AgentRoute.DD });
  };

  const noItemsText = diseasesInPlan?.length ? (
    <NoSuggestionsText>
      Oops... something went wrong. Please try reloading the page. If the
      problem persist, please contact
      <a href="mailto://support@kahun.com">support@kahun.com</a>
    </NoSuggestionsText>
  ) : (
    <NoSuggestionsText>
      No diseases in list, please add a diseases to include in plan
    </NoSuggestionsText>
  );

  return (
    <HistoryAndPhysicalView>
      <MobileOnly>
        <TitleArea>
          <HowItWorks />
          <TitleContainer>
            <BackButtonContainer>
              <BackButton onClick={goBack} className={"back-button"} />
            </BackButtonContainer>
            <Title>Improve Suggestions</Title>
          </TitleContainer>
        </TitleArea>
      </MobileOnly>
      {loading && <ThreeDotsLoader />}
      <HistoryAndPhysicalContainer>
        {!loading && noResults && (
          <Slide direction="up" in mountOnEnter unmountOnExit>
            {noItemsText}
          </Slide>
        )}
        <RecommendedPlanSection
          eventCategory={CATEGORY.IMPROVE_DIAGNOSIS}
          isLoading={loading}
          addChange={addChange}
          category={DiseaseCategoryType.OutcomeSigns}
          suggestions={filteredNextStepSuggestions}
          title={"Does your patient also have?"}
          maxItems={maxItems}
          isEmbedded={false}
        />
      </HistoryAndPhysicalContainer>
    </HistoryAndPhysicalView>
  );
};
